import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';
import {
  aclarar,
  capaDelTema,
  PUNTOS_EN_LA_PORTADA,
  PUNTOS_EN_UNA_CAPA,
  preguntasAlrededor,
  encuadrar,
  etiquetasVisibles,
  radioDe,
  type Caja,
  type LadoDelNombre,
  separarMarcadores,
  subtemasUsables,
  temasParaLaPortada,
  tieneSegundaCapa,
  type MapaDeTemas as Mapa,
  type TemaDelMapa,
} from './datos.js';
import { arrancarLienzo, type Escena, type EstadoDelLienzo } from './lienzo.js';

/** El tema que se abre al entrar: el de más preguntas, para que el mapa sirva sin tocarlo. */
const temaInicial = (temas: readonly TemaDelMapa[]): number => {
  let mejor = 0;
  temas.forEach((t, i) => {
    const m = temas[mejor];
    if (m !== undefined && (t.preguntas.length > m.preguntas.length || (t.preguntas.length === m.preguntas.length && t.hechos > m.hechos))) mejor = i;
  });
  return mejor;
};

// La misma que el viaje del lienzo (lienzo.ts): el bloque y la escena llegan a la vez.
const DURACION_DEL_CRECIMIENTO = 950;
const CURVA = 'cubic-bezier(0.65, 0, 0.35, 1)';
const sinMovimiento = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const esPantallaAncha = () => window.matchMedia('(min-width: 640px)').matches;

/**
 * MAPA-01, 02 y 03: el mapa de temas de la portada.
 *
 * Está para una cosa: que una organizadora entienda en segundos qué puede averiguar y se ponga a
 * preguntar. El recorrido es tema → (subtema) → pregunta → caja rellena, y nada más: ni recuentos,
 * ni fuentes, ni avisos de método (eso es de /admin). Pulsar una pregunta la lleva a la caja **sin
 * enviarla**: enviar cuesta una consulta y eso lo decide ella.
 *
 * Dos niveles como mucho. En escritorio, pulsar un tema con subtemas es un viaje (MAPA-03): el mapa
 * crece hasta ocupar casi toda la pantalla sobre la portada fundida, y a la vez la cámara se acerca
 * al tema y sus puntos se despliegan en sus subtemas (`lienzo.ts`). «Todos los temas» o Escape
 * hacen el viaje al revés y el mapa vuelve a su sitio. Con «reducir movimiento», el cambio es
 * directo. En móvil y para quien no ve el mapa, el tema elegido despliega sus subtemas como botones.
 *
 * Solo una de las dos formas de elegir está en pantalla cada vez (`display: none` saca la otra del
 * árbol de accesibilidad): desde `sm`, los marcadores del mapa, en orden de lectura; en móvil,
 * botones con el nombre, porque en 390 px un dedo no apunta a un punto. El lienzo es decorado.
 */
export function MapaDeTemas({
  mapa: mapaCompleto,
  idioma,
  onElegirPregunta,
}: {
  readonly mapa: Mapa;
  readonly idioma: Idioma;
  readonly onElegirPregunta: (texto: string) => void;
}) {
  const t = diccionarioDe(idioma).mapa;
  const portadaBase = useMemo(() => aclarar(encuadrar(temasParaLaPortada(mapaCompleto)), PUNTOS_EN_LA_PORTADA), [mapaCompleto]);
  const [elegido, setElegido] = useState(() => temaInicial(portadaBase.temas));
  /** El subtema elegido dentro del tema, o -1 si se está mirando el tema entero. */
  const [sub, setSub] = useState(-1);
  /** Si el mapa de escritorio está dentro del tema elegido (segunda capa). */
  const [dentro, setDentro] = useState(false);
  /** Mientras dura el viaje entre capas, los marcadores se esconden: solo se mueve el lienzo. */
  const [viajando, setViajando] = useState(false);
  /** Si el mapa ocupa casi toda la pantalla (solo en escritorio, mientras se está dentro de un tema). */
  const [inmersivo, setInmersivo] = useState(false);
  const [encima, setEncima] = useState(-1);
  const [tamano, setTamano] = useState({ ancho: 0, alto: 0 });

  const lienzo = useRef<HTMLCanvasElement>(null);
  const capaHtml = useRef<HTMLDivElement>(null);
  const bloque = useRef<HTMLDivElement>(null);
  const hueco = useRef<HTMLDivElement>(null);
  const volver = useRef<HTMLButtonElement>(null);
  const escena = useRef<Escena | null>(null);
  const estado = useRef<EstadoDelLienzo>({ elegido, encima: -1, sub, encimaSub: -1 });
  /** Dónde estaba el bloque antes de crecer, para crecer desde ahí y volver ahí. */
  const desde = useRef<DOMRect | null>(null);

  const tema = portadaBase.temas[elegido];
  const subtemas = useMemo(() => (tema === undefined ? [] : subtemasUsables(tema)), [tema]);
  // Los marcadores que caen encima unos de otros se separan unos píxeles, según el tamaño real.
  const portada = useMemo(() => ({ ...portadaBase, temas: separarMarcadores(portadaBase.temas, tamano.ancho, tamano.alto) }), [portadaBase, tamano]);
  const capa = useMemo(() => {
    if (tema === undefined || !dentro) return null;
    const base = aclarar(capaDelTema(portadaBase, tema), PUNTOS_EN_UNA_CAPA);
    return { ...base, temas: separarMarcadores(base.temas, tamano.ancho, tamano.alto) };
  }, [portadaBase, tema, dentro, tamano]);

  // Un solo lienzo para las dos capas: se arranca una vez y se le pasan los datos cuando cambian.
  useEffect(() => {
    if (lienzo.current === null) return;
    const e = arrancarLienzo(lienzo.current, portada, estado.current);
    escena.current = e;
    return () => {
      e.parar();
      escena.current = null;
    };
    // Se arranca una vez; los datos llegan después por ponerPortada y ponerCapa.
  }, []);
  useEffect(() => {
    escena.current?.ponerPortada(portada);
  }, [portada]);
  useEffect(() => {
    if (capa !== null) escena.current?.ponerCapa(capa);
  }, [capa]);

  useEffect(() => {
    estado.current.elegido = elegido;
    estado.current.encima = dentro ? -1 : encima;
    estado.current.sub = sub;
    estado.current.encimaSub = dentro ? encima : -1;
    escena.current?.redibujar();
  }, [elegido, encima, sub, dentro]);

  useEffect(() => {
    const nodo = capaHtml.current;
    if (nodo === null) return;
    const medir = () => {
      setTamano({ ancho: nodo.clientWidth, alto: nodo.clientHeight });
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(nodo);
    return () => {
      observador.disconnect();
    };
  }, []);

  /**
   * Las animaciones del modo inmersivo (MAPA-03), medidas fotograma a fotograma el 2026-09-29 para
   * quitar tres parpadeos:
   * - al cerrar, la animación del bloque acababa sin `fill` y durante un fotograma el bloque volvía
   *   a pantalla completa antes de que React le quitara la clase: ahora se queda en su último
   *   fotograma (`fill: 'forwards'`) y se cancela en el mismo commit que quita la clase;
   * - el fondo fundido desaparecía de golpe al cerrar: ahora se funde con el bloque, misma curva;
   * - al abrir, el relleno, el marco y el alto de la celda del mapa cambiaban de golpe en el primer
   *   fotograma (el lienzo saltaba de 760x440 a 694x486): ahora se animan también, desde lo que
   *   había en la portada.
   */
  const animaciones = useRef<Animation[]>([]);
  const celda = useRef<HTMLDivElement>(null);
  const fondo = useRef<HTMLDivElement>(null);
  const altoCelda = useRef(0);
  const cancelarAnimaciones = () => {
    for (const a of animaciones.current) a.cancel();
    animaciones.current = [];
  };
  const caja = (r: DOMRect) => ({
    top: `${String(r.top)}px`,
    left: `${String(r.left)}px`,
    width: `${String(r.width)}px`,
    height: `${String(r.height)}px`,
  });
  /** El marco del bloque como está en la portada: sin relleno, sin borde visible, sin fondo ni sombra. */
  const marcoCerrado = {
    paddingTop: '0px',
    paddingRight: '0px',
    paddingBottom: '0px',
    paddingLeft: '0px',
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    boxShadow: '0 24px 60px rgba(17, 29, 32, 0)',
  };
  const marcoAbierto = (css: CSSStyleDeclaration) => ({
    paddingTop: css.paddingTop,
    paddingRight: css.paddingRight,
    paddingBottom: css.paddingBottom,
    paddingLeft: css.paddingLeft,
    borderColor: css.borderTopColor,
    backgroundColor: css.backgroundColor,
    boxShadow: css.boxShadow,
  });

  const acercar = (i: number) => {
    setElegido(i);
    setSub(0);
    setDentro(true);
    setViajando(true);
    setEncima(-1);
    if (esPantallaAncha()) {
      desde.current = bloque.current?.getBoundingClientRect() ?? null;
      altoCelda.current = celda.current?.getBoundingClientRect().height ?? 0;
      setInmersivo(true);
    }
  };

  const alejar = () => {
    if (!dentro || viajando) return;
    setViajando(true);
    setEncima(-1);
    const temaAbierto = portadaBase.temas[elegido]?.id;
    // El lienzo, el bloque y el panel vuelven a la portada en el mismo commit: si el bloque soltara el
    // modo inmersivo antes, el hueco encogería con el panel del subtema y el pie saltaría dos veces.
    escena.current?.cerrar(() => {
      setInmersivo(false);
      setDentro(false);
      setViajando(false);
      setSub(-1);
      // El foco vuelve al tema del que se salió, en cuanto su marcador está de nuevo en pantalla.
      requestAnimationFrame(() => {
        if (temaAbierto === undefined) return;
        capaHtml.current?.querySelector<HTMLButtonElement>(`[data-tema="${CSS.escape(temaAbierto)}"]`)?.focus({ preventScroll: true });
      });
    });
    const el = bloque.current;
    const destino = hueco.current?.getBoundingClientRect();
    if (!inmersivo || el === null || destino === undefined || sinMovimiento()) return;
    cancelarAnimaciones();
    const opciones: KeyframeAnimationOptions = { duration: DURACION_DEL_CRECIMIENTO, easing: CURVA, fill: 'forwards' };
    const css = getComputedStyle(el);
    const inicio = el.getBoundingClientRect();
    const c = celda.current;
    const altoInicio = c?.getBoundingClientRect().height ?? 0;
    // Con `fill`, el bloque se queda en su último fotograma hasta que la escena termina y React le
    // quita la clase; la animación se cancela en ese mismo commit.
    animaciones.current.push(
      el.animate(
        [
          { ...caja(inicio), ...marcoAbierto(css) },
          { ...caja(destino), ...marcoCerrado },
        ],
        opciones,
      ),
    );
    if (c !== null)
      animaciones.current.push(c.animate([{ height: `${String(altoInicio)}px` }, { height: `${String(altoCelda.current)}px` }], opciones));
    if (fondo.current !== null) animaciones.current.push(fondo.current.animate([{ opacity: 1 }, { opacity: 0 }], opciones));
  };

  // Al volver a la portada, las animaciones se cancelan en el mismo commit que quita la clase: el
  // navegador pinta ya el estado final, sin un fotograma intermedio.
  useLayoutEffect(() => {
    if (!inmersivo) cancelarAnimaciones();
  }, [inmersivo]);

  // Al entrar: crecer desde donde estaba y empezar el viaje del lienzo con la capa ya puesta.
  useLayoutEffect(() => {
    if (!dentro || !viajando || capa === null) return;
    escena.current?.ponerCapa(capa);
    escena.current?.abrir(elegido, () => {
      setViajando(false);
    });
    const el = bloque.current;
    if (inmersivo && el !== null && desde.current !== null && !sinMovimiento()) {
      cancelarAnimaciones();
      const opciones: KeyframeAnimationOptions = { duration: DURACION_DEL_CRECIMIENTO, easing: CURVA };
      // Todo lo final se mide antes de arrancar ninguna animación: en cuanto el bloque empieza a
      // animarse, su tamaño y el de la celda ya son los del primer fotograma, no los del último (así
      // salió la primera vez: la celda acababa en 534 px y saltaba a 702 al terminar).
      const final = el.getBoundingClientRect();
      const marcoFinal = marcoAbierto(getComputedStyle(el));
      const c = celda.current;
      const altoFinal = c?.getBoundingClientRect().height ?? 0;
      animaciones.current.push(
        el.animate(
          [
            { ...caja(desde.current), ...marcoCerrado },
            { ...caja(final), ...marcoFinal },
          ],
          opciones,
        ),
      );
      if (c !== null)
        animaciones.current.push(c.animate([{ height: `${String(altoCelda.current)}px` }, { height: `${String(altoFinal)}px` }], opciones));
      if (fondo.current !== null) animaciones.current.push(fondo.current.animate([{ opacity: 0 }, { opacity: 1 }], opciones));
    }
    volver.current?.focus({ preventScroll: true });
    // Solo al empezar a entrar.
  }, [dentro]);

  // Escape vuelve a todos los temas; mientras el mapa ocupa la pantalla, la página de detrás no se desplaza.
  useEffect(() => {
    if (!dentro) return;
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        alejar();
        return;
      }
      // Mientras el mapa ocupa la pantalla, el tabulador da vueltas dentro: es un diálogo modal.
      if (e.key !== 'Tab' || !inmersivo || bloque.current === null) return;
      const enfocables = [...bloque.current.querySelectorAll<HTMLElement>('button, a[href]')].filter(
        x => x.offsetParent !== null && !x.closest('.mapa-marcadores-ocultos'),
      );
      const primero = enfocables[0];
      const ultimo = enfocables.at(-1);
      if (primero === undefined || ultimo === undefined) return;
      const fuera = !bloque.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === primero || fuera)) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && (document.activeElement === ultimo || fuera)) {
        e.preventDefault();
        primero.focus();
      }
    };
    document.addEventListener('keydown', alPulsar);
    return () => {
      document.removeEventListener('keydown', alPulsar);
    };
  });
  useEffect(() => {
    document.documentElement.classList.toggle('mapa-sin-desplazar', inmersivo);
    return () => {
      document.documentElement.classList.remove('mapa-sin-desplazar');
    };
  }, [inmersivo]);

  const enPantalla = dentro ? capa : portada;
  const maximo = useMemo(() => Math.max(1, ...(enPantalla?.temas ?? []).map(x => x.hechos)), [enPantalla]);
  const elegidoEnPantalla = dentro ? sub : elegido;
  const conNombre = useMemo(
    () => etiquetasVisibles(enPantalla?.temas ?? [], idioma, tamano.ancho, tamano.alto, enPantalla?.temas[elegidoEnPantalla]?.id),
    [enPantalla, idioma, tamano, elegidoEnPantalla],
  );
  // Orden de lectura para el teclado y los lectores de pantalla: de arriba abajo y de izquierda a derecha.
  const enOrden = useMemo(
    () => (enPantalla?.temas ?? []).map((x, i) => ({ tema: x, i })).sort((a, b) => a.tema.y - b.tema.y || a.tema.x - b.tema.x),
    [enPantalla],
  );

  /**
   * Las cajas de lo que un punto de oro no puede tapar: los nombres fijos y los marcadores, medidos
   * tal como han quedado pintados (el nombre cambia de lado y de ancho según el idioma y el tamaño).
   * Se miden después de pintar y, si cambian, se vuelven a colocar las preguntas.
   */
  const [obstaculos, setObstaculos] = useState<readonly Caja[]>([]);
  useLayoutEffect(() => {
    const nodo = capaHtml.current;
    if (nodo === null) return;
    const base = nodo.getBoundingClientRect();
    const medidas = [...nodo.querySelectorAll('.mapa-nombre-fijo, .mapa-punto')].map(e => {
      const r = e.getBoundingClientRect();
      return {
        x: Math.round(r.left - base.left) - 3,
        y: Math.round(r.top - base.top) - 3,
        ancho: Math.round(r.width) + 6,
        alto: Math.round(r.height) + 6,
      };
    });
    setObstaculos(previas => (JSON.stringify(previas) === JSON.stringify(medidas) ? previas : medidas));
  }, [enPantalla, conNombre, tamano, idioma]);
  const preguntasEnElMapa = useMemo(() => {
    const ocupados: Caja[] = [];
    return enOrden.map(({ tema: x, i }) => ({
      tema: x,
      i,
      puntos: preguntasAlrededor(x, idioma, radioDe(x.hechos, maximo), tamano.ancho, tamano.alto, obstaculos, ocupados),
    }));
  }, [enOrden, idioma, maximo, tamano, obstaculos]);

  if (portadaBase.temas.length === 0 || tema === undefined) return null;
  const actual = sub >= 0 ? subtemas[sub] : undefined;
  const mostrado = actual ?? tema;

  return (
    <section aria-labelledby='mapa-titulo' className='mt-24 flex w-full max-w-[1240px] flex-col items-center'>
      <h2 id='mapa-titulo' className='m-0 text-center font-(family-name:--serif) text-[30px] leading-[1.12] font-bold sm:text-[38px]'>
        {t.titulo}
      </h2>
      <p className='mt-3 mb-0 text-center font-(family-name:--text) text-[19px] text-(--ink-soft)'>{t.resumen}</p>

      {inmersivo ? <div ref={fondo} aria-hidden='true' className='mapa-fondo-inmersivo' onClick={alejar} /> : null}

      {/* El hueco guarda el sitio del bloque mientras este ocupa la pantalla, para que nada salte. */}
      <div ref={hueco} className='mt-6 w-full sm:mt-8' style={inmersivo ? { height: desde.current?.height } : undefined}>
        {/* Desde lg, mapa y preguntas lado a lado: al elegir un tema, sus preguntas cambian a la vista, sin bajar. */}
        <div
          ref={bloque}
          role={inmersivo ? 'dialog' : undefined}
          aria-modal={inmersivo ? true : undefined}
          aria-label={inmersivo ? tema.nombre[idioma] : undefined}
          className={`grid w-full items-center gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-10 ${inmersivo ? 'mapa-inmersivo' : ''}`}
        >
          <div ref={celda} className={`relative h-[180px] w-full ${inmersivo ? 'sm:h-full' : 'sm:h-[440px]'}`}>
            <canvas ref={lienzo} aria-hidden='true' className='mapa-puntos absolute inset-0 h-full w-full' />
            <div
              ref={capaHtml}
              role='group'
              aria-label={dentro ? `${t.mapaEtiqueta} · ${tema.nombre[idioma]}` : t.mapaEtiqueta}
              className={`mapa-marcadores absolute inset-6 hidden sm:block ${viajando ? 'mapa-marcadores-ocultos' : ''}`}
            >
              {enOrden.map(({ tema: x, i }) => (
                <Marcador
                  key={x.id}
                  tema={x}
                  idioma={idioma}
                  radio={radioDe(x.hechos, maximo)}
                  elegido={i === elegidoEnPantalla}
                  lado={conNombre.get(x.id)}
                  conCapa={!dentro && tieneSegundaCapa(x)}
                  textoConCapa={t.conSubtemas}
                  onElegir={() => {
                    if (viajando) return;
                    if (dentro) setSub(i);
                    else if (tieneSegundaCapa(x)) acercar(i);
                    else {
                      setElegido(i);
                      setSub(-1);
                    }
                  }}
                  onEncima={sobre => {
                    setEncima(sobre ? i : -1);
                  }}
                />
              ))}
              {/* Las preguntas de cada tema, como puntos de oro que se pulsan: llevan a la caja con la
                  pregunta escrita, igual que en el panel. Con el teclado solo las del tema elegido:
                  las demás están a un tema de distancia y así no hay sesenta paradas seguidas. */}
              {preguntasEnElMapa.flatMap(({ tema: x, i, puntos }) =>
                puntos.map(p => (
                  <button
                    key={`${x.id}-${p.texto}`}
                    type='button'
                    tabIndex={i === elegidoEnPantalla ? 0 : -1}
                    data-pregunta-de={x.id}
                    className='mapa-pregunta-punto'
                    style={{ left: `${String(p.x * 100)}%`, top: `${String(p.y * 100)}%` }}
                    onClick={() => {
                      onElegirPregunta(p.texto);
                      if (inmersivo) alejar();
                    }}
                    onPointerEnter={() => {
                      setEncima(i);
                    }}
                    onPointerLeave={() => {
                      setEncima(-1);
                    }}
                  >
                    <span aria-hidden='true' className='mapa-pregunta-oro' />
                    <span className='mapa-pregunta-texto'>{p.texto}</span>
                  </button>
                )),
              )}
            </div>
          </div>

          <ul aria-label={t.listaEtiqueta} className='m-0 flex list-none flex-wrap justify-center gap-2 p-0 sm:hidden'>
            {portadaBase.temas.map((x, i) => (
              <li key={x.id}>
                <button
                  type='button'
                  aria-pressed={i === elegido}
                  onClick={() => {
                    setElegido(i);
                    setSub(-1);
                  }}
                  className='mapa-chip cursor-pointer rounded-full border border-(--line-strong) bg-transparent px-4 py-[6px] font-(family-name:--text) text-[16px] text-(--ink-soft)'
                >
                  {x.nombre[idioma]}
                </button>
              </li>
            ))}
          </ul>

          <div aria-live='polite' className='mapa-panel flex w-full flex-col items-center lg:items-start'>
            {dentro ? (
              <button
                ref={volver}
                type='button'
                onClick={alejar}
                className='mapa-volver mb-4 hidden cursor-pointer rounded-full border border-(--line-strong) px-4 py-[6px] font-(family-name:--text) text-[16px] text-(--ink-soft) sm:block'
              >
                ← {t.volver}
              </button>
            ) : null}
            <p className='m-0 font-(family-name:--text) text-[16px] text-(--ink-faint)'>
              {actual === undefined ? t.puedesPreguntar : `${tema.nombre[idioma]} ›`}
            </p>
            <h3 className='m-0 mt-1 mb-4 text-center font-(family-name:--serif) text-[26px] leading-[1.15] font-bold text-(--teal-deep) lg:text-left'>
              {mostrado.nombre[idioma]}
            </h3>

            {/* En móvil, los subtemas del tema elegido se despliegan aquí, como botones. */}
            {subtemas.length >= 2 ? (
              <div className='mb-4 flex w-full flex-col items-center sm:hidden'>
                <p className='m-0 mb-2 font-(family-name:--text) text-[15px] text-(--ink-faint)'>{t.subtemas}</p>
                <ul aria-label={`${t.subtemas}: ${tema.nombre[idioma]}`} className='m-0 flex list-none flex-wrap justify-center gap-2 p-0'>
                  {subtemas.map((s, k) => (
                    <li key={s.id}>
                      <button
                        type='button'
                        aria-pressed={k === sub}
                        onClick={() => {
                          setSub(k === sub ? -1 : k);
                        }}
                        className='mapa-chip cursor-pointer rounded-full border border-(--line-strong) bg-transparent px-3 py-[5px] font-(family-name:--text) text-[15px] text-(--ink-soft)'
                      >
                        {s.nombre[idioma]}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {mostrado.preguntas.length === 0 ? (
              <p className='m-0 text-center font-(family-name:--text) text-[17px] text-(--ink-soft) lg:text-left'>{t.eligeSubtema}</p>
            ) : (
              <ul className='m-0 flex w-full list-none flex-col items-center gap-2 p-0 lg:items-stretch'>
                {mostrado.preguntas.map(p => (
                  <li key={p[idioma]} className='flex justify-center lg:block'>
                    <button
                      type='button'
                      title={t.usar}
                      onClick={() => {
                        onElegirPregunta(p[idioma]);
                        if (inmersivo) alejar();
                      }}
                      className='mapa-pregunta cursor-pointer rounded-full border border-(--line-strong) bg-transparent px-4 py-[7px] text-center font-(family-name:--text) text-[16.5px] leading-[1.4] text-(--ink-soft) lg:w-full lg:rounded-[3px] lg:px-4 lg:py-3 lg:text-left'
                    >
                      {p[idioma]}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* En escritorio, un tema con subtemas elegido desde fuera del mapa también se puede abrir. */}
            {!dentro && subtemas.length >= 2 ? (
              <button
                type='button'
                onClick={() => {
                  acercar(elegido);
                }}
                className='mapa-abrir mt-4 hidden cursor-pointer border-none bg-transparent p-0 font-(family-name:--text) text-[16.5px] text-(--teal-deep) underline underline-offset-4 sm:block'
              >
                {t.verSubtemas} →
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function Marcador({
  tema,
  idioma,
  radio,
  elegido,
  lado,
  conCapa,
  textoConCapa,
  onElegir,
  onEncima,
}: {
  readonly tema: TemaDelMapa;
  readonly idioma: Idioma;
  readonly radio: number;
  readonly elegido: boolean;
  /** Dónde va el nombre; sin lado, solo sale al pasar el ratón o con el foco (debajo). */
  readonly lado: LadoDelNombre | undefined;
  /** Si pulsarlo abre la segunda capa: lleva un anillo más y lo dice a los lectores de pantalla. */
  readonly conCapa: boolean;
  readonly textoConCapa: string;
  readonly onElegir: () => void;
  readonly onEncima: (dentro: boolean) => void;
}) {
  return (
    <button
      type='button'
      aria-pressed={elegido}
      data-tema={tema.id}
      className={`mapa-marcador ${conCapa ? 'mapa-marcador-capa' : ''}`}
      style={{ left: `${String(tema.x * 100)}%`, top: `${String(tema.y * 100)}%` }}
      onClick={onElegir}
      onPointerEnter={() => {
        onEncima(true);
      }}
      onPointerLeave={() => {
        onEncima(false);
      }}
      onFocus={() => {
        onEncima(true);
      }}
      onBlur={() => {
        onEncima(false);
      }}
    >
      <span aria-hidden='true' className='mapa-punto block rounded-full' style={{ width: radio * 2, height: radio * 2 }} />
      <span className={`mapa-nombre mapa-nombre-${lado ?? 'abajo'} ${lado === undefined ? '' : 'mapa-nombre-fijo'}`}>
        {tema.nombre[idioma]}
        {conCapa ? <span className='sr-only'>{textoConCapa}</span> : null}
      </span>
    </button>
  );
}
